/**
 * MixerSerializer
 *
 * Serialization/deserialization for AudioMixerDefinition assets.
 *
 * @module @engine/audio/MixerSerializer
 */

import type { AudioMixerDefinition, AudioMixerBusDefinition, AudioMixerSnapshot } from './types';

const MIXER_VERSION = 1;

/**
 * Create a default mixer definition (Master, Music, SFX, UI, Ambient).
 */
export function createDefaultMixerDefinition(): AudioMixerDefinition {
  const buses: AudioMixerBusDefinition[] = [
    {
      id: 'master',
      name: 'Master',
      volume: 0,
      muted: false,
      solo: false,
      parentBusId: null,
      sends: [],
      filters: [],
      color: '#e2e8f0',
    },
    {
      id: 'music',
      name: 'Music',
      volume: -6,
      muted: false,
      solo: false,
      parentBusId: 'master',
      sends: [],
      filters: [],
      color: '#f59e0b',
    },
    {
      id: 'sfx',
      name: 'SFX',
      volume: 0,
      muted: false,
      solo: false,
      parentBusId: 'master',
      sends: [],
      filters: [],
      color: '#ef4444',
    },
    {
      id: 'ui',
      name: 'UI',
      volume: 0,
      muted: false,
      solo: false,
      parentBusId: 'master',
      sends: [],
      filters: [],
      color: '#3b82f6',
    },
    {
      id: 'ambient',
      name: 'Ambient',
      volume: -3,
      muted: false,
      solo: false,
      parentBusId: 'master',
      sends: [],
      filters: [],
      color: '#10b981',
    },
  ];

  const snapshots: AudioMixerSnapshot[] = [
    {
      id: 'surface',
      name: 'Surface',
      busVolumes: {
        master: 0,
        music: -6,
        sfx: 0,
        ui: 0,
        ambient: -3,
      },
      busFilters: {
        // No filters — clear/dry sound
        master: [],
      },
      transitionTime: 0.8,
    },
    {
      id: 'underwater',
      name: 'Underwater',
      busVolumes: {
        master: -2,
        music: -12,
        sfx: -4,
        ui: 0,        // UI stays audible
        ambient: -6,
      },
      busFilters: {
        // Heavy low-pass on master to muffle everything
        master: [
          { type: 'lowpass', frequency: 600, Q: 1.2, gain: 0, enabled: true },
        ],
        // Extra low-pass on SFX for more underwater feel
        sfx: [
          { type: 'lowpass', frequency: 400, Q: 0.8, gain: 0, enabled: true },
        ],
      },
      transitionTime: 1.2,
    },
  ];

  return {
    version: MIXER_VERSION,
    name: 'Default Mixer',
    buses,
    snapshots,
  };
}

/**
 * Serialize a mixer definition to JSON string.
 */
export function serializeMixer(def: AudioMixerDefinition): string {
  return JSON.stringify(def, null, 2);
}

/**
 * Deserialize a mixer definition from JSON string.
 */
export function deserializeMixer(json: string): AudioMixerDefinition {
  const data = JSON.parse(json) as AudioMixerDefinition;

  // Version migration
  if (!data.version || data.version < MIXER_VERSION) {
    // Future: handle migrations here
  }

  // Ensure required fields
  if (!data.buses || data.buses.length === 0) {
    return createDefaultMixerDefinition();
  }

  // Ensure master bus exists
  const hasMaster = data.buses.some(b => b.parentBusId === null);
  if (!hasMaster) {
    return createDefaultMixerDefinition();
  }

  // Default empty arrays
  data.snapshots = data.snapshots ?? [];
  for (const bus of data.buses) {
    bus.sends = bus.sends ?? [];
    bus.filters = bus.filters ?? [];
    bus.color = bus.color ?? '#8b5cf6';
  }
  for (const snapshot of data.snapshots) {
    snapshot.busFilters = snapshot.busFilters ?? undefined;
  }

  return data;
}
